(ns pfo.mcp.component
  "UI for managing the user's connected external MCP servers: list, add (modal), enable/disable,
   delete. Styled to match the Metrics tab (controls strip + .metrics-box card). State and API calls
   go through the :mcp-connections keechma controller."
  (:require [clojure.string :as str]
            [reagent.core :as reagent :refer [atom]]
            [promesa.core :as p]
            [pfo.collection.model :as collection-model]
            [pfo.controller :as controller]
            [pfo.flash :refer [set-flash]]
            [pfo.api :refer [process-error]]
            [pfo.widget.modal :as modal]
            [pfo.widget.toggler :as toggler]
            [pfo.util :as util]))

;; Default icon shown when a connection has no custom icon configured.
(def default-icon-src "/static/img/icons/mcp.svg")

(defn connection-icon-src
  "The connection's configured icon, or the default MCP icon when the admin didn't set one."
  [conn]
  (let [icon (:icon conn)]
    (if (not-empty icon) icon default-icon-src)))

(defn connection-icon
  "Renders the connection's icon (custom, else the default MCP icon). On image load failure — a broken
   custom data-URL or a missing default asset — falls back to a styled placeholder box rather than the
   browser's broken-image glyph. icon-class is the BEM element class for the surrounding component."
  [_conn icon-class]
  (let [failed? (atom false)]
    (fn [conn icon-class]
      (if @failed?
        [:div {:class (str icon-class " " icon-class "--placeholder")}]
        [:img {:class    icon-class
               :src      (connection-icon-src conn)
               :alt      (or (:name conn) "")
               :on-error (fn [_] (reset! failed? true))}]))))

(defn status-pill [enabled?]
  [:span.c-mcp-connections__status
   {:class (if enabled? "c-mcp-connections__status--on" "c-mcp-connections__status--off")}
   (if enabled? "Enabled" "Disabled")])

(defn valid-url?
  "True when s is an absolute http(s) URL with a host — mirrors the backend's check, for instant
   client-side feedback before submit."
  [s]
  (try
    (let [u (js/URL. (.trim (or s "")))]
      (contains? #{"http:" "https:"} (.-protocol u)))
    (catch :default _ false)))

(defn host-changed?
  "True when the edited URL points at another host than the saved one — mirrors the backend's
   serverHostChanged, which then drops the stored credentials."
  [saved edited]
  (try
    (not= (.-host (js/URL. (.trim (or saved "")))) (.-host (js/URL. (.trim (or edited "")))))
    (catch :default _ true)))

(defn server-error-detail
  "The server's error detail, if any (backend returns {message: …} on discovery/registration failures)."
  [e]
  (not-empty (get-in e [:response :body :message])))

(defn error-block
  "Renders an in-card error. Accepts a plain string (shown as-is) or a map {:summary :detail}: the
   summary is always shown, and a non-empty detail is tucked behind a collapsible \"Details\"."
  [err]
  (cond
    (map? err)
    [:div.c-mcp-card__error
     [:span (:summary err)]
     (when (not-empty (:detail err))
       [:details.c-mcp-card__error-details
        [:summary "Details"]
        [:pre.c-mcp-card__error-detail (:detail err)]])]
    (some? err)
    [:div.c-mcp-card__error err]))

(defn auth-type-label
  "Human-readable label for a connection's auth type (None / Bearer / OAuth 2.0)."
  [auth-type]
  (case auth-type
    "oauth2" "OAuth 2.0"
    "bearer" "Bearer"
    "none" "None"
    auth-type))

(defn scope-label
  "Global / Family label for a connection's credential-ownership scope. A none-auth connection needs no
   credential (Global); bearer/oauth2 reflect the license admin's choice."
  [conn]
  (if (= (:auth_type conn) "none")
    "Global"
    (case (:oauth_mode conn)
      "shared" "Global"
      "family" "Family"
      nil)))

(defn icon-field
  "Drag-and-drop (or click) an image; stored as a base64 data URL in the icon atom."
  [_icon]
  (let [input-ref (atom nil)]
    (fn [icon]
      (let [read-file (fn [file]
                        (cond
                          (nil? file) nil
                          (not (.startsWith (.-type file) "image/")) (set-flash :error "Please choose an image file.")
                          (> (.-size file) 262144) (set-flash :error "Icon is too large (max 256 KB).")
                          :else (let [r (js/FileReader.)]
                                  (set! (.-onload r) (fn [e] (reset! icon (.. e -target -result))))
                                  (.readAsDataURL r file))))]
        [:div.c-mcp-icon-drop
         {:on-drag-over #(.preventDefault %)
          :on-drop      (fn [e] (.preventDefault e) (read-file (.item (.. e -dataTransfer -files) 0)))
          :on-click     #(when @input-ref (.click @input-ref))}
         (if (not-empty @icon)
           [:img.c-mcp-icon-drop__preview {:src @icon}]
           [:span.c-mcp-icon-drop__hint "Drag an image here, or click to choose"])
         [:input {:type "file" :accept "image/*" :style {:display "none"}
                  :ref       #(reset! input-ref %)
                  :on-change (fn [e] (read-file (.item (.. e -target -files) 0)))}]
         (when (not-empty @icon)
           [:button.c-button.c-button--small
            {:type     :button
             :on-click (fn [e] (.stopPropagation e) (.preventDefault e) (reset! icon ""))}
            "Remove"])]))))

(defn slug-tool-prefix
  "Proposes a tool prefix from a connection name: lowercase, other characters to `_`, `mcp` when
   empty or for a leading digit, at most 20 characters."
  [conn-name]
  (let [slug (-> (str/lower-case (or conn-name ""))
                 (str/replace #"[^a-z0-9]+" "_")
                 (str/replace #"^_+|_+$" ""))
        slug (cond (str/blank? slug) "mcp"
                   (re-find #"^[0-9]" slug) (str "mcp_" slug)
                   :else slug)]
    (str/replace (subs slug 0 (min 20 (count slug))) #"_+$" "")))

(defn connection-form [{:keys [name icon url saved-url auth-type token client-id client-secret oauth-mode lax-discovery
                               tool-prefix prefix-edited? errors editing? on-cancel on-save]}]
  (let [repointed? (and editing? (host-changed? @saved-url @url))]
    [:form.c-form--edit.c-form--edit--modal.c-mcp-modal
     [:h3.u-margin-bottom (if editing? "Edit MCP server" "Add MCP server")]
     [:fieldset
      [:dl
       [:dt "Name"]
       [:dd [:input.u-width--full {:type "text" :value @name
                                   :on-change (fn [e]
                                                ((util/reset-from-event name) e)
                                                ;; On add, the prefix follows the name until the admin edits it.
                                                (when-not (or editing? @prefix-edited?)
                                                  (reset! tool-prefix (slug-tool-prefix @name))))}]]
       [:dt "Tool name prefix"]
       [:dd [:input.u-width--full {:type "text" :value @tool-prefix
                                   :on-change (fn [e]
                                                (reset! tool-prefix (str/lower-case (.. e -target -value)))
                                                (reset! prefix-edited? true)
                                                (swap! errors dissoc :tool_prefix))}]
        [:p.u-color--secondary "AARK sees this server's tools as mcp__<prefix>_<id>__<tool>. Lowercase letters, digits and single _, starting with a letter."]
        (when (:tool_prefix @errors) [:span.error (:tool_prefix @errors)])]
       [:dt "Server URL"]
       [:dd [:input.u-width--full {:type "text" :placeholder "https://server/mcp" :value @url
                                   :on-change (fn [e]
                                                ((util/reset-from-event url) e)
                                                (swap! errors dissoc :url))}]
        (when (:url @errors) [:span.error (:url @errors)])]
       [:dt "Icon"]
       [:dd [icon-field icon]]
       [:dt "Authentication"]
       [:dd [:select {:value @auth-type :on-change (util/reset-from-event auth-type)}
             [:option {:value "none"} "None"]
             [:option {:value "bearer"} "Bearer token"]
             [:option {:value "oauth2"} "OAuth 2.0"]]]]
      (when (#{"bearer" "oauth2"} @auth-type)
        [:dl
         [:dt "Credential ownership"]
         [:dd [:select {:value @oauth-mode :on-change (util/reset-from-event oauth-mode)}
               [:option {:value "shared"} "Global (shared — one credential)"]
               [:option {:value "family"} "Family-controlled (each family admin decides)"]]
          (when (:oauth_mode @errors) [:span.error (:oauth_mode @errors)])]])
      ;; A shared (global) bearer connection's token is set here by the license admin. A family-controlled
      ;; one is set later, per family, from the Apps view — so no token field here.
      (when (and (= @auth-type "bearer") (= @oauth-mode "shared"))
        [:dl
         [:dt "Bearer token"]
         [:dd [:input.u-width--full {:type "password" :value @token
                                     :placeholder (cond repointed? "Required for the new host"
                                                        editing?   "Leave blank to keep current")
                                     :on-change (util/reset-from-event token)}]
          (when (:bearer_token @errors) [:span.error (:bearer_token @errors)])]])
      (when (= @auth-type "oauth2")
        [:dl
         [:dt "Client ID"]
         [:dd [:input.u-width--full {:type "text" :value @client-id
                                     :placeholder "leave blank for dynamic registration"
                                     :on-change (util/reset-from-event client-id)}]]
         [:dt "Client secret"]
         [:dd [:input.u-width--full {:type "password" :value @client-secret
                                     :placeholder (cond repointed? "optional — the current one is not kept for the new host"
                                                        editing?   "leave blank to keep current"
                                                        :else      "optional")
                                     :on-change (util/reset-from-event client-secret)}]]
         [:dt "Discovery"]
         [:dd [:label.o-flex-align-items-center
               [:input {:type "checkbox" :checked @lax-discovery
                        :on-change (fn [e] (reset! lax-discovery (.. e -target -checked)))}]
               [:span.u-margin-left--xs "Relaxed OAuth discovery — only for trusted servers that fail standard discovery. Skips issuer and resource identity checks."]]]])]
     [:footer.o-space-between
      [:button {:type :button :on-click (util/handler on-cancel)} "Cancel"]
      [:button.c-button {:type :submit :on-click (util/handler on-save)} (if editing? "Save" "Add")]]]))

(defn server-row [conn _on-toggle _on-delete _on-edit _on-authorize _auth-error]
  ;; form-2: the toggle's :checked atom is seeded from the connection's enabled state and re-synced
  ;; below when the server value changes (e.g. after a reload), so it never shows a stale value.
  (let [checked      (atom (:enabled conn))
        last-enabled (atom (:enabled conn))]
    (fn [conn on-toggle on-delete on-edit on-authorize auth-error]
      (when (not= @last-enabled (:enabled conn))
        (reset! last-enabled (:enabled conn))
        (reset! checked (:enabled conn)))
      [:tr
       [:td
        [:a.c-mcp-connections__name.u-cursor {:href "#" :title "Edit server"
                                              :on-click (util/handler #(on-edit conn))}
         [connection-icon conn "c-mcp-connections__icon"]
         (:name conn)]]
       [:td [:a {:href (:url conn) :target "_blank" :rel "noopener noreferrer"} (:url conn)]]
       [:td [:code (:tool_prefix conn)]]
       [:td (auth-type-label (:auth_type conn))]
       ;; Scope: the credential-ownership mode (none-auth is effectively global).
       [:td (scope-label conn)]
       [:td
        [:div.c-mcp-connections__status-cell
         [status-pill (:enabled conn)]
         [toggler/toggler {:checked checked :size "small" :on-change (fn [_] (on-toggle conn checked))}]
         [:a.c-mcp-connections__delete.u-color--danger-red
          {:href "#" :title "Delete server"
           :on-click (util/handler #(on-delete (:uid conn)))}
          "×"]]]
       ;; Authorize is only needed for a global (shared) oauth2 connection — the license admin
       ;; authorizes its single token here. Family/per-user are handled from the Apps view.
       [:td.c-mcp-connections__authorize
        (when (and (= (:auth_type conn) "oauth2") (= (:oauth_mode conn) "shared"))
          [:div
           [:button.c-button.c-button--small
            {:title "Start the OAuth authorization flow"
             :on-click (util/handler #(on-authorize conn))}
            (if (:oauth_authorized conn) "Reauthorize" "Authorize")]
           (when auth-error [error-block auth-error])])]])))

(defn connections-list []
  (let [conns       (controller/subscribe :mcp-connections)
        errors      (atom {})
        modal-state (atom {:enabled false})
        editing-uid (atom nil)
        name        (atom "")
        icon        (atom "")
        url         (atom "")
        saved-url   (atom "")
        auth-type   (atom "none")
        token       (atom "")
        client-id   (atom "")
        client-secret (atom "")
        oauth-mode  (atom "shared")
        lax-discovery (atom false)
        tool-prefix (atom "")
        prefix-edited? (atom false)
        reset-form  (fn []
                      (reset! editing-uid nil)
                      (reset! name "")
                      (reset! icon "")
                      (reset! url "")
                      (reset! saved-url "")
                      (reset! auth-type "none")
                      (reset! token "")
                      (reset! client-id "")
                      (reset! client-secret "")
                      (reset! oauth-mode "shared")
                      (reset! lax-discovery false)
                      (reset! tool-prefix "")
                      (reset! prefix-edited? false)
                      (reset! errors {}))
        close-modal (fn [] (swap! modal-state assoc :enabled false) (reset-form))
        open-modal  (fn [] (reset-form) (swap! modal-state assoc :enabled true))
        open-edit   (fn [conn]
                      (reset! editing-uid (:uid conn))
                      (reset! name (:name conn))
                      (reset! icon (or (:icon conn) ""))
                      (reset! url (:url conn))
                      (reset! saved-url (:url conn))
                      (reset! auth-type (or (:auth_type conn) "none"))
                      (reset! token "")
                      (reset! client-id (or (:oauth_client_id conn) ""))
                      (reset! client-secret "")
                      (reset! oauth-mode (or (not-empty (:oauth_mode conn)) "shared"))
                      (reset! lax-discovery (boolean (:lax_oauth_discovery conn)))
                      (reset! tool-prefix (or (:tool_prefix conn) ""))
                      (reset! errors {})
                      (swap! modal-state assoc :enabled true))
        load        (fn [] (controller/dispatch :mcp-connections :load {}))
        on-save     (fn []
                      (if-not (valid-url? @url)
                        (swap! errors assoc :url "Enter a valid http(s) URL.")
                      (let [editing @editing-uid
                            params  {:name            @name
                                     :icon            @icon
                                     :url             @url
                                     :auth_type       @auth-type
                                     :bearer_token    (when (= @auth-type "bearer") @token)
                                     :oauth_client_id (when (= @auth-type "oauth2") @client-id)
                                     :oauth_mode      (when (#{"bearer" "oauth2"} @auth-type) @oauth-mode)
                                     :client_secret   (when (= @auth-type "oauth2") @client-secret)
                                     :lax_oauth_discovery (and (= @auth-type "oauth2") @lax-discovery)
                                     :tool_prefix     @tool-prefix}
                            done    (fn [_]
                                      (swap! modal-state assoc :enabled false)
                                      (reset-form)
                                      (set-flash :success (if editing "MCP server updated." "MCP server connected."))
                                      (load))]
                        (if editing
                          (controller/dispatch :mcp-connections :edit
                                               {:uid editing :params params :errors-atom errors :on-success done})
                          (controller/dispatch :mcp-connections :create
                                               {:params params :errors-atom errors :on-success done})))))
        authorize-errors (atom {})
        on-authorize (fn [conn]
                       (swap! authorize-errors dissoc (:uid conn))
                       (controller/dispatch :mcp-connections :authorize
                                            {:uid      (:uid conn)
                                             :on-error (fn [e]
                                                         (swap! authorize-errors assoc (:uid conn)
                                                                {:summary "Could not connect to server"
                                                                 :detail  (server-error-detail e)}))}))
        on-delete   (fn [uid]
                      (controller/dispatch :mcp-connections :delete
                                           {:uid        uid
                                            :on-success (fn [_]
                                                          (set-flash :success "MCP server removed.")
                                                          (load))}))
        on-toggle   (fn [conn checked]
                      (controller/dispatch :mcp-connections :update
                                           {:uid        (:uid conn)
                                            :params     {:enabled (not (:enabled conn))}
                                            :on-success (fn [_] (load))
                                            ;; Revert the optimistic toggle if the update failed, so the
                                            ;; switch can't show a state the server never accepted.
                                            :on-error   (fn [_] (reset! checked (:enabled conn)))}))]
    (load)
    (fn []
      [:div.u-padding-bottom
       [:div.metrics-box.c-mcp-connections
        [:div.o-flex-align--space-between.o-flex-align-items-center
         [:h2 "MCP Servers"]
         [:button.c-button {:on-click (util/handler open-modal)} "Add MCP server"]]
        [:div.u-margin-top
         (if (empty? @conns)
           [:h4.u-color--secondary "No MCP servers connected."]
           [:table
            [:thead
             [:tr [:th "Name"] [:th "URL"] [:th "Tool prefix"] [:th "Auth"] [:th "Scope"] [:th "Status"] [:th]]]
            [:tbody
             (doall
              (for [conn @conns]
                ^{:key (:uid conn)} [server-row conn on-toggle on-delete open-edit on-authorize (get @authorize-errors (:uid conn))]))]])]]
       [modal/modal
        [connection-form {:name name :icon icon :url url :saved-url saved-url :auth-type auth-type :token token
                          :client-id client-id :client-secret client-secret :oauth-mode oauth-mode
                          :lax-discovery lax-discovery :tool-prefix tool-prefix :prefix-edited? prefix-edited?
                          :errors errors :editing? (boolean @editing-uid) :on-cancel close-modal :on-save on-save}]
        modal-state]])))

(defn integrations
  "The Integrations settings page. Hosts the MCP Servers section (and is the home for future
   integration sections)."
  []
  [:div
   [connections-list]])

(defn family-access-card [conn _on-toggle]
  ;; form-2: the toggle's :checked atom is seeded from the family's current grant and re-synced below
  ;; whenever the server value changes.
  (let [checked      (atom (:granted conn))
        last-granted (atom (:granted conn))]
    (fn [conn on-toggle]
      ;; Re-sync to the server value when it changes. The MCP pool is global, so navigating between
      ;; families reuses the same card instance (keyed by uid) with a different grant; without this the
      ;; toggle would show the previous family's state. We compare against the last server value (not
      ;; @checked) so an in-flight optimistic toggle is not clobbered.
      (when (not= @last-granted (:granted conn))
        (reset! last-granted (:granted conn))
        (reset! checked (:granted conn)))
      ;; A connection disabled by the license admin is greyed out (util/disable-tool).
      [util/disable-tool
       {:disabled  (not (:enabled conn))
        :message   "This server is disabled"
        :component [:div.c-mcp-card {:class (when-not @checked "is-disabled")}
                    [connection-icon conn "c-mcp-card__icon"]
                    [:div.c-mcp-card__name (:name conn)]
                    [:div.c-mcp-card__meta (str (auth-type-label (:auth_type conn)) " (" (scope-label conn) ")")]
                    [:div.c-mcp-card__toggle
                     [toggler/toggler {:checked checked :size "small" :on-change (fn [_] (on-toggle conn checked))}]]]}])))

(defn family-access
  "Family card section: a license admin grants/revokes this family's access to the configured MCP
   servers (one box per server with its icon and an on/off toggle). Users of the family then get the
   granted servers' tools in AARK."
  [family-id]
  (let [conns     (controller/subscribe :mcp-family-connections)
        load      (fn [] (controller/dispatch :mcp-family-connections :load {:family-id family-id}))
        on-toggle (fn [conn checked]
                    (controller/dispatch :mcp-family-connections :set
                                         {:family-id  family-id
                                          :uid        (:uid conn)
                                          :enabled    (not (:granted conn))
                                          :on-success (fn [_] (load))
                                          ;; Revert the optimistic toggle if the grant update failed.
                                          :on-error   (fn [_] (reset! checked (:granted conn)))}))]
    (load)
    (fn []
      [:div.metrics-box.c-mcp-connections
       [:h2 "Apps"]
       (if (empty? @conns)
         [:p.u-color--secondary "No MCP servers configured."]
         [:div.c-mcp-cards
          (doall
           (for [conn @conns]
             ^{:key (:uid conn)} [family-access-card conn on-toggle]))])])))

;; --- Connected Apps (user-facing) ---------------------------------------------------------------

(defn app-status
  "Status pill for an app card: Connected / Not connected, matching the rest of the UI."
  [configured?]
  [:span.c-mcp-connections__status
   {:class (if configured? "c-mcp-connections__status--on" "c-mcp-connections__status--off")}
   (if configured? "Connected" "Not connected")])

(defn app-card
  "One app on the family Apps view, rendered according to the acting user's role and the connection's
   auth/ownership model. A family admin gets an on/off toggle for the whole family (independent of the
   license admin's grant); when off, the rest of the controls are hidden. When on:
   - none: just shown as available (no credential needed).
   - global (shared) bearer/oauth: managed by the license admin — read-only status.
   - family-controlled: the family admin picks the sub-mode (Family access / Per-user). When acting on
     the credential is allowed (family sub-mode for admins, per-user for any member) the card shows the
     action — an OAuth Connect button or a bearer token field — otherwise a read-only status."
  [_conn _handlers]
  ;; form-2: token input for bearer; the enable toggle's atom is re-synced to the server value below.
  (let [token           (atom "")
        editing?        (atom false)
        enabled-checked (atom (:family_enabled _conn))
        last-enabled    (atom (:family_enabled _conn))]
    (fn [conn {:keys [on-set-mode on-set-token on-connect on-set-enabled error]}]
      (let [{:keys [auth_type oauth_mode auth_mode is_admin self_managed configured family_enabled]} conn
            family? (= oauth_mode "family")
            ;; The acting user may set the credential when it is a per-user app (their own) or a family
            ;; app and they administer the family.
            can-act (or self_managed (and (= auth_mode "family") is_admin))
            ;; Bearer token entry: hidden behind an "Edit token" button once a token is saved, so the
            ;; saved state is clear and the value is only re-entered on purpose.
            token-form (fn []
                         [:div.c-mcp-card__token
                          [:input.c-mcp-card__token-input
                           {:type "password" :placeholder "Enter token" :value @token
                            :on-change (util/reset-from-event token)}]
                          [:div.c-mcp-card__token-actions
                           [:button.c-button.c-button--small
                            {:disabled (empty? @token)
                             :on-click (util/handler #(when (seq @token)
                                                        (on-set-token conn @token)
                                                        (reset! token "")
                                                        (reset! editing? false)))}
                            "Save"]
                           (when configured
                             [:button.c-button.c-button--small
                              {:on-click (util/handler #(do (reset! token "") (reset! editing? false)))}
                              "Cancel"])]])
            action  (fn []
                      (cond
                        (not= auth_type "bearer")
                        [:button.c-button.c-button--small {:on-click (util/handler #(on-connect conn))}
                         (if configured "Reconnect" "Connect")]
                        ;; Bearer with a saved token: show an explicit edit affordance, not the raw field.
                        (and configured (not @editing?))
                        [:button.c-button.c-button--small {:on-click (util/handler #(reset! editing? true))}
                         "Edit token"]
                        :else
                        [token-form]))]
        ;; Re-sync the toggle to the server value (the card instance is reused across family switches).
        (when (not= @last-enabled family_enabled)
          (reset! last-enabled family_enabled)
          (reset! enabled-checked family_enabled))
        [:div.c-mcp-card {:class (when (and is_admin (not @enabled-checked)) "is-disabled")}
         [connection-icon conn "c-mcp-card__icon"]
         [:div.c-mcp-card__name (:name conn)]
         [:div.c-mcp-card__meta (str (auth-type-label auth_type) " (" (scope-label conn) ")")]
         ;; Family admin's on/off switch for the family — top-right corner of the card.
         (when is_admin
           [:div.c-mcp-card__toggle
            [toggler/toggler {:checked enabled-checked :size "small" :on-change (fn [_] (on-set-enabled conn enabled-checked))}]])
         (cond
           ;; Off for the family (only an admin reaches this — members don't see disabled apps).
           (and is_admin (not family_enabled))
           [:span.u-color--secondary "Off for this family."]
           ;; No credential required.
           (= auth_type "none")
           [:span.c-mcp-connections__status.c-mcp-connections__status--on "Available"]
           ;; Global (shared): the license admin configures it; members just see the status.
           (not family?)
           [app-status configured]
           ;; Family-controlled.
           :else
           (let [mode (or auth_mode "")]
             [:<>
              ;; 1. Status indicator first.
              (cond
                (= mode "") (if is_admin
                              [:span.c-mcp-connections__status.c-mcp-connections__status--off "Disabled"]
                              [:span.u-color--secondary "Not set up yet."])
                :else [app-status configured])
              ;; 2. Sub-mode chooser (family admin) — below the status.
              (when is_admin
                [:select {:value     mode
                          :on-change (fn [ev] (on-set-mode conn (util/event-value ev)))}
                 [:option {:value ""} "Choose access…"]
                 [:option {:value "family"} "Family access"]
                 [:option {:value "per_user"} "Per-user"]])
              ;; 3. Action (authorize / token) or a read-only note.
              (cond
                (= mode "") nil
                can-act [action]
                :else [:span.u-color--secondary "Provided by your family."])]))
         (when error [error-block error])]))))

(defn connected-apps
  "The Apps page: pick a family you belong to and see every app the license admin enabled for it. The
   options adapt to your role — a family admin configures each app (sub-mode, family authorization /
   token); a regular member only sees the apps and can log in for per-user apps."
  [params]
  (let [families    (atom [])
        selected    (atom nil)
        apps        (controller/subscribe :mcp-family-apps)
        errors      (atom {})
        load-apps   (fn [fid] (when fid (controller/dispatch :mcp-family-apps :load {:family-id fid})))
        on-set-mode (fn [conn mode]
                      (swap! errors dissoc (:uid conn))
                      (controller/dispatch :mcp-family-apps :set-mode
                                           {:uid        (:uid conn)
                                            :family-id  @selected
                                            :auth-mode  mode
                                            :on-success (fn [_] (load-apps @selected))
                                            :on-error   (fn [_]
                                                          (swap! errors assoc (:uid conn)
                                                                 "Couldn't save this setting. Please try again."))}))
        on-set-token (fn [conn tok]
                       (swap! errors dissoc (:uid conn))
                       (controller/dispatch :mcp-family-apps :set-token
                                            {:uid        (:uid conn)
                                             :family-id  @selected
                                             :token      tok
                                             :on-success (fn [_]
                                                           (set-flash :success "Token saved.")
                                                           (load-apps @selected))
                                             :on-error   (fn [_]
                                                           (swap! errors assoc (:uid conn)
                                                                  "Couldn't save the token. Please try again."))}))
        on-connect  (fn [conn]
                      (swap! errors dissoc (:uid conn))
                      (controller/dispatch :mcp-family-apps :authorize
                                           {:uid       (:uid conn)
                                            :family-id @selected
                                            :on-error  (fn [e]
                                                         (swap! errors assoc (:uid conn)
                                                                {:summary "Could not connect to server"
                                                                 :detail  (server-error-detail e)}))}))
        on-set-enabled (fn [conn checked]
                         (swap! errors dissoc (:uid conn))
                         (controller/dispatch :mcp-family-apps :set-enabled
                                              {:uid        (:uid conn)
                                               :family-id  @selected
                                               :enabled    (not (:family_enabled conn))
                                               :on-success (fn [_] (load-apps @selected))
                                               ;; Revert the optimistic toggle if the update failed.
                                               :on-error   (fn [_] (reset! checked (:family_enabled conn)))}))]
    ;; family_id (when present) comes back from the OAuth callback so the chooser restores to the family
    ;; that was being authorized; otherwise default to the first family.
    (let [want-fid (some-> (:family_id (:query-params params)) util/str->int)]
      (->> (collection-model/collection-list-promise nil false)
           (p/map (fn [resp]
                    (reset! families (vec resp))
                    (let [ids (set (map :id resp))
                          fid (if (contains? ids want-fid) want-fid (:id (first resp)))]
                      (when fid
                        (reset! selected fid)
                        (load-apps fid)))))
           (process-error)))
    (case (:mcp_oauth (:query-params params))
      "ok"  (set-flash :success "App connected.")
      nil   nil
      (set-flash :error "Authorization failed. Please try again."))
    (fn [_]
      [:div.u-padding-bottom
       [:div.metrics-box.c-mcp-connections
        [:div.o-flex-align--space-between.o-flex-align-items-center
         [:h2 "Apps"]
         (when (seq @families)
           [:select {:value     (or @selected "")
                     :on-change (fn [ev]
                                  (let [fid (util/str->int (util/event-value ev))]
                                    (reset! selected fid)
                                    (load-apps fid)))}
            (doall (for [f @families] ^{:key (:id f)} [:option {:value (:id f)} (:name f)]))])]
        [:div.u-margin-top
         (cond
           (empty? @families)
           [:h4.u-color--secondary "You don't belong to any family yet."]
           (empty? @apps)
           [:h4.u-color--secondary "No apps available for this family."]
           :else
           [:div.c-mcp-cards
            ;; Key by family + uid so switching the family selector remounts each card with fresh local
            ;; state (e.g. the bearer token input), rather than reusing a card across families.
            (doall (for [c @apps]
                     ^{:key (str @selected "-" (:uid c))}
                     [app-card c {:on-set-mode    on-set-mode
                                  :on-set-token   on-set-token
                                  :on-connect     on-connect
                                  :on-set-enabled on-set-enabled
                                  :error          (get @errors (:uid c))}]))])]]])))
